import React, { useState, useEffect } from 'react';
import axios from '../utils/axios';
import { Phone, Check, X, Loader2, MessageSquare, Shield, AlertCircle, Copy, Trash2, Plus } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

interface WhatsAppNumber {
  id: number;
  phoneNumber: string;
  countryCode: string;
  fullNumber: string;
  verified: boolean;
  verificationCode?: string;
  verificationSentAt?: string;
  createdAt: string;
  isPrimary: boolean;
}

interface WhatsAppManagerProps {
  menuId?: string | number;
}

const WhatsAppManager: React.FC<WhatsAppManagerProps> = ({ menuId }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [whatsappNumbers, setWhatsappNumbers] = useState<WhatsAppNumber[]>([]);
  const [newNumber, setNewNumber] = useState('');
  const [countryCode, setCountryCode] = useState('+39'); // Default to Italy
  const [verificationCode, setVerificationCode] = useState('');
  const [selectedNumberId, setSelectedNumberId] = useState<number | null>(null);
  const [showVerificationModal, setShowVerificationModal] = useState(false);

  // Country codes for dropdown
  const countryCodes = [
    { code: '+39', name: 'Italy', flag: '🇮🇹' },
    { code: '+33', name: 'France', flag: '🇫🇷' },
    { code: '+34', name: 'Spain', flag: '🇪🇸' },
    { code: '+49', name: 'Germany', flag: '🇩🇪' },
    { code: '+44', name: 'UK', flag: '🇬🇧' },
    { code: '+1', name: 'USA/Canada', flag: '🇺🇸' },
    { code: '+61', name: 'Australia', flag: '🇦🇺' },
    { code: '+55', name: 'Brazil', flag: '🇧🇷' },
    { code: '+91', name: 'India', flag: '🇮🇳' },
    { code: '+86', name: 'China', flag: '🇨🇳' },
    { code: '+81', name: 'Japan', flag: '🇯🇵' },
    { code: '+82', name: 'South Korea', flag: '🇰🇷' },
  ];

  useEffect(() => {
    if (menuId) {
      fetchWhatsAppNumbers();
    }
  }, [menuId]);

  const fetchWhatsAppNumbers = async () => {
    if (!menuId) return;

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/menu/${menuId}/whatsapp-numbers`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setWhatsappNumbers(response.data.data);
      }
    } catch (error: any) {
      console.error('Failed to fetch WhatsApp numbers:', error);
      if (error.response?.status !== 404) {
        toast.error(t('messages.error'));
      }
    } finally {
      setLoading(false);
    }
  };

  const validatePhoneNumber = (phone: string): boolean => {
    // Remove all non-digit characters
    const digitsOnly = phone.replace(/\D/g, '');
    // Italian numbers: 10 digits starting with 3
    if (countryCode === '+39') {
      return digitsOnly.length === 10 && digitsOnly.startsWith('3');
    }
    // General validation: at least 5 digits
    return digitsOnly.length >= 5 && digitsOnly.length <= 15;
  };

  const handleAddNumber = async () => {
    if (!menuId) {
      toast.error(t('whatsapp.manager.messages.menu_id_required'));
      return;
    }

    if (!newNumber.trim()) {
      toast.error(t('whatsapp.manager.messages.enter_phone_number'));
      return;
    }

    if (!validatePhoneNumber(newNumber)) {
      toast.error(t('whatsapp.manager.messages.valid_phone_number'));
      return;
    }

    // Check if number already exists
    const fullNumber = `${countryCode}${newNumber.replace(/\D/g, '')}`;
    const exists = whatsappNumbers.some(num => num.fullNumber === fullNumber);
    if (exists) {
      toast.error(t('whatsapp.manager.messages.number_already_registered'));
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `/menu/${menuId}/whatsapp-numbers`,
        {
          phoneNumber: newNumber.replace(/\D/g, ''),
          countryCode: countryCode,
          isPrimary: whatsappNumbers.length === 0 // First number becomes primary
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        const newNumberData = response.data.data;
        setWhatsappNumbers(prev => [...prev, newNumberData]);
        setNewNumber('');
        
        // Show verification modal for the new number
        setSelectedNumberId(newNumberData.id);
        setShowVerificationModal(true);
        
        toast.success(t('whatsapp.manager.messages.number_added_verify'));
      }
    } catch (error: any) {
      console.error('Failed to add phone number:', error);
      toast.error(error.response?.data?.message || 'Failed to add phone number');
    } finally {
      setSaving(false);
    }
  };

  const sendVerificationCode = async (numberId: number) => {
    if (!menuId) return;

    setVerifying(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `/menu/${menuId}/whatsapp-numbers/${numberId}/send-verification`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setSelectedNumberId(numberId);
        setShowVerificationModal(true);
        toast.success(t('whatsapp.manager.messages.verification_code_sent'));
      }
    } catch (error: any) {
      console.error('Failed to send verification code:', error);
      toast.error(error.response?.data?.message || 'Failed to send verification code');
    } finally {
      setVerifying(false);
    }
  };

  const verifyNumber = async () => {
    if (!menuId || !selectedNumberId || !verificationCode.trim()) {
      toast.error(t('whatsapp.manager.messages.enter_verification_code_prompt'));
      return;
    }

    setVerifying(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `/menu/${menuId}/whatsapp-numbers/${selectedNumberId}/verify`,
        { verificationCode: verificationCode.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        // Update the number in state
        setWhatsappNumbers(prev => 
          prev.map(num => 
            num.id === selectedNumberId 
              ? { ...num, verified: true, verificationCode: undefined }
              : num
          )
        );
        
        setVerificationCode('');
        setShowVerificationModal(false);
        setSelectedNumberId(null);
        
        toast.success(t('whatsapp.manager.messages.number_verified_success'));
      }
    } catch (error: any) {
      console.error('Failed to verify number:', error);
      toast.error(error.response?.data?.message || t('whatsapp.manager.messages.invalid_verification_code'));
    } finally {
      setVerifying(false);
    }
  };

  const removeNumber = async (numberId: number) => {
    if (!menuId) return;

    if (!window.confirm(t('messages.confirm_delete'))) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(
        `/menu/${menuId}/whatsapp-numbers/${numberId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setWhatsappNumbers(prev => prev.filter(num => num.id !== numberId));
        toast.success(t('whatsapp.manager.messages.number_removed_success'));
      }
    } catch (error: any) {
      console.error('Failed to remove phone number:', error);
      toast.error(error.response?.data?.message || 'Failed to remove phone number');
    }
  };

  const setAsPrimary = async (numberId: number) => {
    if (!menuId) return;

    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `/menu/${menuId}/whatsapp-numbers/${numberId}/primary`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        // Update all numbers: set this one as primary, others as not primary
        setWhatsappNumbers(prev => 
          prev.map(num => ({
            ...num,
            isPrimary: num.id === numberId
          }))
        );
        toast.success(t('whatsapp.manager.messages.primary_number_updated'));
      }
    } catch (error: any) {
      console.error('Failed to set primary number:', error);
      toast.error(error.response?.data?.message || 'Failed to update primary number');
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success(t('whatsapp.manager.messages.copied_to_clipboard'));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="w-8 h-8 animate-spin text-[#7BD5B5]" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-[#0A0C0B] mb-2">{t('whatsapp.manager.title')}</h2>
        <p className="text-[#687d76]">
          {t('whatsapp.manager.subtitle')}
        </p>
      </div>

      {/* Add New Number Form */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-[#0A0C0B] mb-4">{t('whatsapp.manager.add_phone_number')}</h3>
        
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-[#0A0C0B] mb-2">
              {t('whatsapp.manager.country_code')}
            </label>
            <select
              value={countryCode}
              onChange={(e) => setCountryCode(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#7BD5B5] focus:border-transparent"
            >
              {countryCodes.map(country => (
                <option key={country.code} value={country.code}>
                  {country.flag} {country.name} ({country.code})
                </option>
              ))}
            </select>
          </div>
          
          <div className="flex-1">
            <label className="block text-sm font-medium text-[#0A0C0B] mb-2">
              {t('whatsapp.manager.phone_number')}
            </label>
            <input
              type="tel"
              value={newNumber}
              onChange={(e) => setNewNumber(e.target.value)}
              placeholder={t('whatsapp.manager.phone_number_placeholder')}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#7BD5B5] focus:border-transparent"
            />
          </div>
          
          <div className="flex items-end">
            <button
              onClick={handleAddNumber}
              disabled={saving || !newNumber.trim()}
              className="px-6 py-2 bg-[#0A0C0B] text-white rounded-lg font-medium hover:bg-[#1A1C1B] disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {t('whatsapp.manager.adding')}
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4" />
                  {t('whatsapp.manager.add_number')}
                </>
              )}
            </button>
          </div>
        </div>
        
        <p className="text-sm text-[#687d76] mt-3">
          {t('whatsapp.manager.verification_instructions')}
        </p>
      </div>

      {/* Phone Numbers List */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-[#0A0C0B] mb-4">{t('whatsapp.manager.registered_numbers')}</h3>
        
        {whatsappNumbers.length === 0 ? (
          <div className="text-center py-8">
            <Phone className="w-12 h-12 text-[#7BD5B5]/40 mx-auto mb-4" />
            <p className="text-gray-500">{t('whatsapp.manager.no_numbers_registered')}</p>
            <p className="text-sm text-gray-400 mt-1">
              {t('whatsapp.manager.add_number_above')}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {whatsappNumbers.map(number => (
              <div 
                key={number.id}
                className={`p-4 border rounded-lg ${number.isPrimary ? 'border-green-200 bg-green-50' : 'border-gray-200'}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-full ${number.verified ? 'bg-green-100 text-green-600' : 'bg-yellow-100 text-yellow-600'}`}>
                      {number.verified ? <Check className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-[#0A0C0B]">{number.fullNumber}</span>
                        {number.isPrimary && (
                          <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">
                            {t('whatsapp.manager.primary')}
                          </span>
                        )}
                        {!number.verified && (
                          <span className="px-2 py-1 text-xs bg-yellow-100 text-yellow-800 rounded-full">
                            {t('whatsapp.manager.unverified')}
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-[#687d76]">
                        {t('whatsapp.manager.added')} {new Date(number.createdAt).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    {!number.verified && (
                      <button
                        onClick={() => sendVerificationCode(number.id)}
                        disabled={verifying}
                        className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 flex items-center gap-1 disabled:opacity-50"
                      >
                        <Shield className="w-3 h-3" />
                        {t('whatsapp.manager.verify')}
                      </button>
                    )}
                    
                    {!number.isPrimary && number.verified && (
                      <button
                        onClick={() => setAsPrimary(number.id)}
                        className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                      >
                        {t('whatsapp.manager.set_as_primary')}
                      </button>
                    )}
                    
                    <button
                      onClick={() => copyToClipboard(number.fullNumber)}
                      className="p-2 text-gray-500 hover:text-gray-700"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                    
                    <button
                      onClick={() => removeNumber(number.id)}
                      className="p-2 text-gray-500 hover:text-red-600"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                
                {!number.verified && (
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <p className="text-sm text-yellow-700 mb-2">
                      {t('whatsapp.manager.verification_sent')}
                    </p>
                    <button
                      onClick={() => {
                        setSelectedNumberId(number.id);
                        setShowVerificationModal(true);
                      }}
                      className="text-sm text-blue-600 hover:text-blue-800"
                    >
                      {t('whatsapp.manager.enter_verification_code')}
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Instructions */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h3 className="font-semibold text-blue-800 mb-3 flex items-center gap-2">
          <MessageSquare className="w-5 h-5" />
          {t('whatsapp.manager.how_it_works')}
        </h3>
        <ul className="space-y-2 text-blue-700">
          <li className="flex items-start gap-2">
            <Check className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>{t('whatsapp.manager.instructions.add_numbers')}</span>
          </li>
          <li className="flex items-start gap-2">
            <Check className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>{t('whatsapp.manager.instructions.verify_security')}</span>
          </li>
          <li className="flex items-start gap-2">
            <Check className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>{t('whatsapp.manager.instructions.set_primary')}</span>
          </li>
          <li className="flex items-start gap-2">
            <Check className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>{t('whatsapp.manager.instructions.send_updates')}</span>
          </li>
        </ul>
      </div>

      {/* Verification Modal */}
      {showVerificationModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-[#0A0C0B]">{t('whatsapp.manager.verification_modal_title')}</h3>
              <button
                onClick={() => {
                  setShowVerificationModal(false);
                  setVerificationCode('');
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <p className="text-gray-600 mb-4">
              {t('whatsapp.manager.verification_instructions')}
            </p>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#0A0C0B] mb-2">
                  {t('whatsapp.manager.verification_code')}
                </label>
                <input
                  type="text"
                  value={verificationCode}
                  onChange={(e) => {
                    // Only allow digits and limit to 6
                    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                    setVerificationCode(value);
                  }}
                  placeholder={t('whatsapp.manager.verification_code_placeholder')}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#7BD5B5] focus:border-transparent text-center text-2xl tracking-widest"
                />
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowVerificationModal(false);
                    setVerificationCode('');
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  {t('whatsapp.manager.cancel')}
                </button>
                <button
                  onClick={verifyNumber}
                  disabled={verifying || verificationCode.length !== 6}
                  className="flex-1 px-4 py-2 bg-[#0A0C0B] text-white rounded-lg hover:bg-[#1A1C1B] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {verifying ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin inline mr-2" />
                      {t('whatsapp.manager.verifying')}
                    </>
                  ) : (
                    t('whatsapp.manager.verify_number')
                  )}
                </button>
              </div>
              
              <button
                onClick={() => selectedNumberId && sendVerificationCode(selectedNumberId)}
                disabled={verifying}
                className="text-sm text-blue-600 hover:text-blue-800 disabled:opacity-50"
              >
                {t('whatsapp.manager.resend_verification_code')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WhatsAppManager;